import { useLazyQuery } from '@apollo/client';
import {
  UserByIdQuery,
  UserByIdResponse,
  UserReadAction,
} from '@shared/Authentication/shared/domain';
import { ResponseApolloErrorMapping } from '@shared/index';
import {
  BaseQueryResponse2,
  ResourceNotFoundError,
  Response,
} from '@shared/models';
import {
  GET_USER_INFORMATION,
  UserInformationRequest,
  UserInformationResponse,
} from '../schemas/queries/user-information.query';
import { useCallback, useMemo } from 'react';

const gqlUserReadActions: UserReadAction = {
  useGetUserById: (): BaseQueryResponse2<UserByIdQuery, UserByIdResponse> => {
    const [action, { data, loading, error, refetch }] = useLazyQuery<
      UserInformationResponse,
      UserInformationRequest
    >(GET_USER_INFORMATION, { errorPolicy: 'all' });

    const result = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data
          ? Response.notPerformed
          : !data.userInformation?.id
          ? new Response.Failure(new ResourceNotFoundError('userById'))
          : new Response.Success(new UserByIdResponse(data.userInformation)),
      [data, error]
    );

    const recall = useCallback(
      async (): Promise<void> => refetch().then(() => void 0),
      [refetch]
    );

    const get = useCallback(
      (query: UserByIdQuery) =>
        action({
          variables: {
            userId: query.user.value,
          },
        }),
      []
    );

    return {
      loading,
      result,
      recall,
      get,
    };
  },
};

export const userReadActions = gqlUserReadActions;
